import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CompanyModel } from '@data-layer/models/companies';
import { BillItemModel, BillModel, PaymentMethodModel } from '@data-layer/models/payments';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumPaymentBillType, enumPaymentProvider } from '@data-layer/enums/payments';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CompaniesService } from '@services/companies.service';
import { Observable } from 'rxjs';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { SubscriptionModel } from '@app/data-layer/models/common';
import { OrganizationsService } from '@services/organizations.service';

@Component({
  selector: 'company-payment-method',
  templateUrl: './company-payment-method.component.html',
  styleUrls: ['./company-payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyPaymentMethodComponent implements OnInit, OnChanges {
  @Input() public company: CompanyModel;
  @Input() public organization: OrganizationModel;
  @Input() public subscription: SubscriptionModel;
  @Input() public allPaymentMethods: PaymentMethodModel[] = [];
  @Input() public paymentMethods: PaymentMethodModel[] = [];
  @Input() public pendingBills: BillModel[] = [];
  @Input() public isLoading: boolean;
  @Input() public isOrganization: boolean = false;
  @Input() public isPayPalPaymentTokenLoading: boolean;
  @Input() public addPaymentMethodText: string = 'ADD_PAYMENT_METHOD';
  @Output() public resetSelectedSubscription: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deletePayment: EventEmitter<enumPaymentProvider> = new EventEmitter<enumPaymentProvider>();
  @Output() public paypalPaymentToken: EventEmitter<void> = new EventEmitter<void>();
  @Output() public stripePaymentToken: EventEmitter<void> = new EventEmitter<void>();
  @Output() public payNow: EventEmitter<number> = new EventEmitter<number>();

  public formGroup: UntypedFormGroup;
  public isAddPaymentMethod: boolean;
  public isSelectPaymentMethod: boolean;
  public isAddingPaymentMethod: boolean;
  public isDeletePaymentMethod: boolean;
  public isStripePaymentAvailable: boolean;
  public isPayPalPaymentAvailable: boolean;

  public enumPaymentBillType: typeof enumPaymentBillType = enumPaymentBillType;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;
  private nonUSCodeId: number = 3;

  constructor(private organizationsService: OrganizationsService, private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.paypalPaymentToken.emit();
    this.stripePaymentToken.emit();
    this.formGroup = new UntypedFormGroup({
      paymentMethod: new UntypedFormControl(null, Validators.required),
    });

    this.updatePaymentMethods();

    if (this.isOrganization ? !this.organization?.defaultPaymentMethod?.id : !this.company?.defaultPaymentMethodId) {
      this.setIsAddPaymentMethod(true);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.paymentMethods) {
      this.updatePaymentMethods();
    }

    if (!this.isOrganization && changes.company) {
      this.setIsAddPaymentMethod(!this.company?.defaultPaymentMethodId);
    }

    if (this.isOrganization && changes.organization) {
      this.setIsAddPaymentMethod(!this.organization?.defaultPaymentMethod?.id);
    }

    if (this.isOrganization && changes.subscription) {
      if (this.isShowSelectedSubscription) {
        this.scrollToHeader();
        this.setIsAddPaymentMethod(false);
        this.setIsSelectPaymentMethod(true);
      }
    }
  }

  public setIsAddPaymentMethod(val: boolean): void {
    this.isAddPaymentMethod = val;
  }

  public setIsSelectPaymentMethod(val: boolean): void {
    this.isSelectPaymentMethod = val;
  }

  public setIsAddingPaymentMethod(val: boolean): void {
    this.isAddingPaymentMethod = val;
  }

  public setIsDeletePaymentMethod(val: boolean): void {
    this.isDeletePaymentMethod = val;
  }

  public onAddPayment(): void {
    this.setIsSelectPaymentMethod(false);
    this.setIsAddingPaymentMethod(true);

    switch (this.currentPaymentMethod?.provider) {
      case enumPaymentProvider.STRIPE:
        this.isStripePaymentAvailable = true;
        break;
      case enumPaymentProvider.PAYPAL:
        this.isPayPalPaymentAvailable = true;
        break;
      default:
        break;
    }
  }

  public onPaymentMethodsClose() {
    if (
      (this.isOrganization && !this.organization?.defaultPaymentMethod?.id) ||
      (!this.isOrganization && !this.company.defaultPaymentMethodId)
    ) {
      this.setIsAddPaymentMethod(true);
      this.setIsSelectPaymentMethod(false);
    } else {
      this.setIsSelectPaymentMethod(false);
    }

    if (this.isShowSelectedSubscription) {
      this.resetSelectedSubscription.emit();
    }
  }

  public updateCurrentPayment(): void {
    this.setIsSelectPaymentMethod(true);
  }

  public payBill(): void {
    this.payNow.emit(this.pendingBills?.[0]?.id);
  }

  public deletePaymentMethod(): void {
    if (this.isOrganization) {
      this.deletePayment.emit(this.currentOrganizationPaymentMethod?.provider);
    } else {
      this.deletePayment.emit(this.currentCompanyPaymentMethod?.provider);
    }
    this.setIsDeletePaymentMethod(false);
  }

  public get pendingSum(): number {
    return (this.pendingBills?.[0]?.items || []).reduce((prev: number, curr: BillItemModel) => curr.quantity * curr.price + prev, 0);
  }

  public get currentPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.formGroup.get('paymentMethod').value);
  }

  public get currentCompanyPaymentMethodIcon(): string {
    return (this.allPaymentMethods || []).find((item: PaymentMethodModel) => item.id === this.company.defaultPaymentMethodId)?.icon;
  }

  public get currentCompanyPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.company.defaultPaymentMethodId);
  }

  public get currentOrganizationPaymentMethodIcon(): string {
    return (this.allPaymentMethods || []).find((item: PaymentMethodModel) => item.id === this.organization?.defaultPaymentMethod?.id)?.icon;
  }

  public get currentOrganizationPaymentMethod(): PaymentMethodModel {
    return (this.paymentMethods || []).find((item: PaymentMethodModel) => item.id === this.organization?.defaultPaymentMethod?.id);
  }

  public get currentUrl(): string {
    return window.location.href;
  }

  public get isPendingFailed$(): Observable<BillModel> {
    return this.isOrganization ? this.organizationsService.organizationFailedBill$ : this.companiesService.companyFailedBill$;
  }

  public get isShowSelectedSubscription(): boolean {
    return this.isOrganization && !!this.subscription && !this.organization?.defaultPaymentMethod?.id;
  }

  private updatePaymentMethods(): void {
    if (!this.paymentMethods?.length || !this.formGroup?.get('paymentMethod')) {
      return;
    }

    if (this.isOrganization && ((this.company?.isAllowedForCustoms && !this.company?.isUnitedStates) || this.company?.isUnitedStates)) {
      this.formGroup
        .get('paymentMethod')
        .setValue(this.paymentMethods.find((item: PaymentMethodModel) => item.isDefault)?.id || this.paymentMethods[0]?.id);
    } else {
      this.formGroup
        .get('paymentMethod')
        .setValue(this.paymentMethods.find((item: PaymentMethodModel) => item.codeId === this.nonUSCodeId)?.id);
    }
  }

  private scrollToHeader() {
    const element: HTMLElement = document.getElementById('company-payment-method-header');

    if (element) {
      setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
  }
}
